import {
  Anchor,
  IconChartLineUp,
  IconCirclesThreePlus,
  IconCurrencyCircleDollar,
  IconGift,
  IconHandbagSimple,
  IconList,
  IconProps,
  IconUsers,
  Stack,
  Text,
  Tooltip,
  csx,
} from '@vtex/admin-ui'
import { useAdmin } from '@vtex/raccoon-next'
import React from 'react'
import type { MessageDescriptor } from 'react-intl'
import { defineMessages, useIntl } from 'react-intl'

import type { ColumnParams, SimplifiedPromotion } from 'typings'

export const PromotionTypeIcons: Record<string, React.FC<IconProps>> = {
  regular: IconCurrencyCircleDollar,
  combo: IconHandbagSimple,
  forThePriceOf: IconCirclesThreePlus,
  progressive: IconChartLineUp,
  buyAndWin: IconGift,
  campaign: IconUsers,
  MultipleEffects: IconList,
  maxPricePerItem: IconCurrencyCircleDollar,
}

export const discountTypeMessages: Record<string, MessageDescriptor> =
  defineMessages({
    regular: { id: 'admin/promotions.list.promotion-type.regular' },
    combo: { id: 'admin/promotions.list.promotion-type.combo' },
    forThePriceOf: {
      id: 'admin/promotions.list.promotion-type.more-for-less',
    },
    progressive: { id: 'admin/promotions.list.promotion-type.progressive' },
    buyAndWin: { id: 'admin/promotions.list.promotion-type.buy-and-win' },
    MultipleEffects: {
      id: 'admin/promotions.list.promotion-type.multiple-effects',
    },
    campaign: { id: 'admin/promotions.list.promotion-type.campaign' },
    maxPricePerItem: {
      id: 'admin/promotions.list.promotion-type.max-price-per-item',
    },
  })

export const TextEllipsisStyle = {
  width: 'full',
  display: 'inline-block',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
}

type PromotionIconProps = {
  type: string
}

function PromotionIcon(props: PromotionIconProps) {
  const { type } = props
  const { formatMessage } = useIntl()
  const Icon = PromotionTypeIcons[type] ?? PromotionTypeIcons.regular

  const messageDescriptor = discountTypeMessages[type]

  const label = messageDescriptor ? formatMessage(messageDescriptor) : type

  return (
    <Tooltip text={label}>
      <Icon className={csx({ marginRight: '$space-3', color: '$info' })} />
    </Tooltip>
  )
}

export function NameAndDescription(props: ColumnParams<SimplifiedPromotion>) {
  const {
    item: { id, name, description, type },
  } = props

  const { account } = useAdmin()

  return (
    <Anchor
      href={`https://${account}.myvtex.com/admin/promotions/${id}`}
      className={csx({
        textDecoration: 'none',
        ':hover': { textDecoration: 'none' },
      })}
      onClick={(e: React.MouseEvent) => {
        if (e.metaKey) {
          e.stopPropagation()
        } else {
          e.preventDefault()
        }
      }}
    >
      <span
        className={csx({
          height: 'full',
          display: 'flex',
          alignItems: 'center',
          flexDirection: 'row',
        })}
      >
        <PromotionIcon type={type} />
        <Stack className={csx({ height: 'full', justifyContent: 'center' })}>
          <Text variant="action1" title={name}>
            {name}
          </Text>
          <Text
            tone="secondary"
            title={description}
            className={csx({ ':empty': { display: 'none' } })}
          >
            {description}
          </Text>
        </Stack>
      </span>
    </Anchor>
  )
}
